import * as React from 'react';
import { Check, Copy, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function initialsOf(name: string | null | undefined, email: string): string {
  const src = name?.trim();
  if (src) {
    return src
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.slice(0, 2).toUpperCase();
}

export function UserAvatar({
  name,
  email,
  picture,
  className,
}: {
  name: string | null | undefined;
  email: string;
  picture?: string | null;
  className?: string;
}) {
  return (
    <Avatar className={cn('h-8 w-8 shrink-0', className)}>
      <AvatarImage src={picture ?? undefined} alt="" />
      <AvatarFallback className="bg-foreground/[0.06] font-mono text-[11px] font-semibold text-foreground">
        {initialsOf(name, email)}
      </AvatarFallback>
    </Avatar>
  );
}

/** Small mono copy button used inline with a value. */
export function CopyButton({
  value,
  label = 'Copy',
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={className}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

/**
 * Confirmation in a full dialog (never a toast). Stays open while `onConfirm`
 * runs; optional type-to-confirm for irreversible actions.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  danger,
  typeToConfirm,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  typeToConfirm?: string;
  onConfirm: () => Promise<void> | void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [typed, setTyped] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setTyped('');
      setBusy(false);
    }
  }, [open]);

  const matches = !typeToConfirm || typed.trim().toLowerCase() === typeToConfirm.toLowerCase();

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-muted-foreground">{body}</div>
          </DialogDescription>
        </DialogHeader>
        {typeToConfirm && (
          <div className="space-y-2">
            <Label htmlFor="confirm-typed">
              Type <span className="font-mono text-foreground">{typeToConfirm}</span> to confirm
            </Label>
            <Input
              id="confirm-typed"
              autoComplete="off"
              className="font-mono"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={danger ? 'destructive' : 'default'}
            disabled={busy || !matches}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                onOpenChange(false);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One-time secret moment (temporary password, recovery codes): a full dialog
 * that cannot be dismissed by clicking outside, with copy + optional download.
 */
export function SecretDialog({
  open,
  onClose,
  title,
  description,
  values,
  mono = true,
  downloadName,
  confirmLabel = 'Done',
  requireAck = true,
  ackLabel = 'I have stored this somewhere safe',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: React.ReactNode;
  values: { label?: string; value: string }[];
  mono?: boolean;
  downloadName?: string;
  confirmLabel?: string;
  requireAck?: boolean;
  ackLabel?: string;
}) {
  const [acked, setAcked] = React.useState(false);
  React.useEffect(() => {
    if (!open) setAcked(false);
  }, [open]);

  const all = values.map((v) => v.value).join('\n');
  const grid = values.length > 2 && !values.some((v) => v.label);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (!requireAck || acked) && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (requireAck && !acked) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className={cn(grid ? 'grid grid-cols-2 gap-2' : 'space-y-3')}>
          {values.map((v, i) => (
            <div key={i} className="min-w-0">
              {v.label && (
                <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                  {v.label}
                </p>
              )}
              <code
                className={cn(
                  'block break-all rounded-md border border-border bg-foreground/[0.04] px-2.5 py-2 text-[13px]',
                  mono && 'font-mono',
                  grid && 'text-center'
                )}
              >
                {v.value}
              </code>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <CopyButton value={all} label={values.length > 1 ? 'Copy all' : 'Copy'} />
          {downloadName && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const url = URL.createObjectURL(new Blob([all + '\n'], { type: 'text/plain' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = downloadName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="h-3.5 w-3.5" />
              Download .txt
            </Button>
          )}
        </div>
        {requireAck && (
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox checked={acked} onCheckedChange={(c) => setAcked(c === true)} />
            {ackLabel}
          </label>
        )}
        <DialogFooter>
          <Button type="button" disabled={requireAck && !acked} onClick={onClose}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
